import { clamp } from "../utils/math";
import { periodForUnixSeconds } from "./save/defaults";
import type { OrbitForgeSave } from "./save/types";

export const DAILY_IGNITION_DURATION_SECONDS = 600;
export const DAILY_STREAK_MULTIPLIERS = [1.1, 1.25, 1.5, 2, 3, 5, 7] as const;

export interface DailyIgnitionView {
  readonly streakDay: number;
  readonly multiplier: number;
  readonly remainingSeconds: number;
  readonly available: boolean;
  readonly active: boolean;
  readonly used: boolean;
}

export function syncDailyIgnition(save: OrbitForgeSave, nowUnixSeconds: number, prestigeLevel: number): boolean {
  const dayId = periodForUnixSeconds(nowUnixSeconds).dayId;
  if (save.stats.DailyIgnitionLastDayId === dayId) {
    return false;
  }

  const lastDayId = save.stats.DailyIgnitionLastDayId;
  const continuedYesterday = lastDayId === dayId - 1;
  const usedPrestigeGrace = prestigeLevel > 0 && lastDayId === dayId - 2;
  const nextStreak = continuedYesterday || usedPrestigeGrace
    ? Math.min(DAILY_STREAK_MULTIPLIERS.length, Math.max(1, Math.floor(save.stats.DailyIgnitionStreak)) + 1)
    : 1;

  save.stats.DailyIgnitionStreak = nextStreak;
  save.stats.DailyIgnitionLastDayId = dayId;
  save.stats.DailyIgnitionRemainingSeconds = DAILY_IGNITION_DURATION_SECONDS;
  save.stats.DailyIgnitionActive = 0;
  return true;
}

export function consumeDailyIgnition(save: OrbitForgeSave, deltaSeconds: number): boolean {
  if (save.stats.DailyIgnitionActive <= 0 || save.stats.DailyIgnitionRemainingSeconds <= 0 || deltaSeconds <= 0) {
    return false;
  }

  const nextRemaining = Math.max(0, save.stats.DailyIgnitionRemainingSeconds - deltaSeconds);
  const changed = nextRemaining !== save.stats.DailyIgnitionRemainingSeconds;
  save.stats.DailyIgnitionRemainingSeconds = nextRemaining;
  if (nextRemaining <= 0) {
    save.stats.DailyIgnitionActive = 0;
  }
  return changed;
}

export function claimDailyIgnition(save: OrbitForgeSave): boolean {
  if (save.stats.DailyIgnitionActive > 0 || save.stats.DailyIgnitionRemainingSeconds <= 0) {
    return false;
  }
  save.stats.DailyIgnitionActive = 1;
  save.stats.DailyIgnitionClaims += 1;
  return true;
}

export function dailyIgnitionMultiplier(save: OrbitForgeSave): number {
  return save.stats.DailyIgnitionActive > 0 && save.stats.DailyIgnitionRemainingSeconds > 0 ? multiplierForStreak(save.stats.DailyIgnitionStreak) : 1;
}

export function buildDailyIgnitionView(save: OrbitForgeSave): DailyIgnitionView {
  const multiplier = dailyIgnitionMultiplier(save);
  const remainingSeconds = Math.max(0, save.stats.DailyIgnitionRemainingSeconds);
  const active = multiplier > 1;
  return {
    streakDay: normalizeStreakDay(save.stats.DailyIgnitionStreak),
    multiplier,
    remainingSeconds,
    available: !active && remainingSeconds > 0,
    active,
    used: save.stats.DailyIgnitionStreak > 0 && remainingSeconds <= 0
  };
}

export function multiplierForStreak(streakDay: number): number {
  return DAILY_STREAK_MULTIPLIERS[normalizeStreakDay(streakDay) - 1];
}

export function formatStreakMultiplier(multiplier: number): string {
  if (Number.isInteger(multiplier)) {
    return `x${multiplier.toFixed(0)}`;
  }
  return `x${multiplier.toFixed(2).replace(/0$/, "")}`;
}

function normalizeStreakDay(streakDay: number): number {
  return Math.round(clamp(Number.isFinite(streakDay) ? streakDay : 1, 1, DAILY_STREAK_MULTIPLIERS.length));
}
